import * as angular from "angular"
import { FieldViewModel } from "../fields"
import { BiomassUnit, LayerTypeGroupId, LayerTypeId, YieldLayerSubtype } from "../generated/enums"
import { LocaleString } from "../types"
import { groupBy } from "../utils/array"
import { escape } from "../utils/regexp"
import { isArray, isNumber, isObject, isUndefinedOrNull } from "../utils/utils"
import { DatalayerZone } from "./datalayerZone"
import { IDataLayer, IDatalayerContext, IDatalayerKeyItem, IDatalayersLoader, IDataLayerStyle, IDataLayerType, IDataLayerZone, IDatalayerZoneMapInstance, ILayerViewModel, ZoneTextLocation } from "./types"

export function isTerraMapLayer(t: IDataLayer | IDataLayerType) {

    if (isLayer(t)) {
        t = t.Type
    }

    if (isType(t)) {
        return (t.GroupId == LayerTypeGroupId.TerraMap)
    }

}

function isLayer(a: any): a is IDataLayer {
    return isObject(a) && isType(a.Type)
}

function isType(t: any): t is IDataLayerType {
    return isObject(t) && isNumber(t.TypeId) && isNumber(t.GroupId)
}

export function isMapLayerZone(arg: any): arg is IDatalayerZoneMapInstance {
    return arg.id && arg.zone && arg.layer && arg.layer.context
}

export function getDefaultZoneText(zone: DatalayerZone, location: ZoneTextLocation = ZoneTextLocation.MapFeature): string {

    let data = zone.zone.Data

    if (data) {

        let keys = Object.keys(data)

        if (keys.length) {

            let text = (data[keys[0]] || "").toString()
            if (location == ZoneTextLocation.MapTooltip) text = keys[0] + ": " + text

            return text

        }

    }

}

export function getSelectedFillColor(color: number[], opacity: number): number[] {

    if (isArray(color)) {

        color = color.slice()
        let alpha = opacity / 100

        switch (color.length) {
            case 4:
                color[3] = alpha
                return color

            case 3:
                color.push(alpha)
                return color

        }
    }

    return color

}

export function getKeyAsRanges(key: IDatalayerKeyItem[], suffix: string) {

    if (!key) return key

    let groups = groupBy(key, k => k.color)

    key = groups
        .map(g => {

            let v1 = g.values[0].value
            let v2 = g.values[g.values.length - 1].value

            let text = (v1 != v2 ? `${v1} - ${v2}${suffix}` : `${v1}${suffix}`)

            return {
                color: g.key,
                text: text,
                value: v1
            }

        })
        .sort((a, b) => a.value - b.value)

    return key

}

export function cloneDatalayerZone(zone: IDataLayerZone) {

    const newZone: IDataLayerZone = angular.extend({}, zone)

    newZone.DataLayerZoneId = 0
    newZone.Geometry = null
    newZone.Value = null
    newZone.Data = null
    newZone.InitValue = null

    newZone.RegularShape = angular.copy(zone.RegularShape)
    newZone.Style = angular.copy(zone.Style)

    return newZone

}



export function getLayerDescription(l: IDataLayer, cropping?: FieldViewModel): string {

    let t = getLayerTypeName(l) || ""

    if (l.Satellite) {

        return getSatelliteLayerDescription(l, cropping)

    }
    else if (l.Name) {

        if (l.Type && l.Type.ExcludeTypeFromName) {
            return l.Name;
        }

        let re = new RegExp(escape(t), "i");

        if (!re.test(l.Name)) {
            t = l.Name + " - " + t;
        } else {
            t = l.Name;
        }

    }

    else if ((l.TypeId == LayerTypeId.GenericPlan) && (!!l.GenericPlan)) {

        return l.GenericPlan.Name + " - product plan"

    }

    else if (l.Type && cropping && cropping.isCropping()) {

        const cropName = cropping.getCropName()

        if (cropName) {

            if ((!l.Type.ExcludeCroppingFromName) || cropping.hasSiblings()) {
                t += " - " + cropName
            }

            if (l.TypeId == LayerTypeId.Yield) {
                return `${cropName} - Yield - ${cropping.cropping.YearId}`
            }

        }

    }
    else if (l.Type && l.Type.IncludeYearInName && l.FirstYearId) {

        let yt = l.FirstYearId.toString();
        if (t.indexOf(yt) <= 0) {
            t = t + " - " + yt;
        }

    }

    if ((!l.Name) && l.TerraMap && (l.TypeId == LayerTypeId.SoilType)) {
        t = "Soil texture (TerraMap)"
    }

    return t;

}


/**
 * Returns a layer description for any satellite-based layer
 * @param layer Layer
 * @param cropping Cropping
 */
function getSatelliteLayerDescription(layer: IDataLayer, cropping?: FieldViewModel) {

    let type = ""

    switch (layer.TypeId) {

        case LayerTypeId.Biomass:
            type = BiomassUnit[layer.Unit]
            break

        case LayerTypeId.Picture:
            type = "RGB"
            break

    }

    return [
        type,
        layer.Date?.toLocaleDateString(),
        cropping?.getCropName()
    ].filter(t => !!t).join(" - ")

}

export function getLargestZone<T extends { area: number }>(layer: { zones: T[] }): T {

    const zones = layer.zones

    if (!(zones && zones.length)) {
        return
    }

    let largest = zones[0];

    if (zones.length > 1) {

        let areas = zones.map(z => {
            return {
                zone: z,
                area: z.area
            }
        });

        areas = areas.sort((a1, a2) => a2.area - a1.area);

        largest = areas[0].zone;

    }

    return largest;

}

export function getStyleProp<T>(from: { type: IDataLayerType }, prop: (style: IDataLayerStyle) => T, defaultValue: T) {
    const style = from && from.type ? from.type.Style : undefined
    const value = style ? prop(style) : undefined
    return isUndefinedOrNull(value) ? defaultValue : value
}

export function getLayerTypeName(l: IDataLayer): string {

    if (!l) return null;
    let type = l.Type;

    if (!type) return null;

    switch (type.TypeId) {

        // TODO: provide all of this from the API, somehow

        case LayerTypeId.Yield:

            switch (l.SubtypeId) {

                case YieldLayerSubtype.ActualYield:
                    return "Yield" as LocaleString;

                case YieldLayerSubtype.TargetYield:
                    return "Yield potential" as LocaleString

            }

        default:
            if (type.Name) {
                return type.Name;
            }

            return `Type #${type.TypeId}`


    }
}


export function runOnLayersLoaded(layers: ILayerViewModel[], callback: () => void) {

    let pending = layers.length

    if (!pending) {
        callback()
    }

    const onLoaded = () => {
        if ((--pending) == 0) {
            callback()
        }
    }

    layers.forEach(l => l.notifyOnLoad(onLoaded))

}

export function showLayers(ctxt: IDatalayerContext, layers: ILayerViewModel[], callback?: () => void): void
export function showLayers(loader: IDatalayersLoader, layers: ILayerViewModel[], callback?: () => void): void
export function showLayers(a: IDatalayerContext | IDatalayersLoader, layers: ILayerViewModel[], callback?: () => void): void {

    const loader: IDatalayersLoader = a["loader"] || a

    const notloaded = layers.filter(l => !l.zonesLoaded)

    const done = () => {
        layers.forEach(l => l.visible = true)
        callback && callback()
    }

    if (notloaded.length) {
        loader.loadLayerZones(layers.map(l => l.datalayer)).then(() => done())
    }
    else {
        done()
    }

}
